import {Banner, Button, FormControl, Textarea, TextInput} from '@primer/react'
import {AriaAlert} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'

import {isValidColor, LabelColorPicker, normalizeHexColor, randomHexColor} from './components/LabelColorPicker'
import {LABELS} from './constants/labels'
import {LabelPreview} from './LabelPreview'
import {type CreateLabelMutationLabel, useCreateLabelMutation} from './mutations/use-create-label-mutation'
import styles from './RepositoryLabel.module.css'
import {validateLabelColor, validateLabelForm} from './utils/validate-label-form'

export type InlineLabelFormLabel = CreateLabelMutationLabel

export type InlineLabelFormRepository = {
  id: string
}

export const InlineLabelFormVariant = {
  Default: 'default',
  Picker: 'picker',
} as const

export type InlineLabelFormVariant = (typeof InlineLabelFormVariant)[keyof typeof InlineLabelFormVariant]

export type InlineLabelFormProps = {
  repository: InlineLabelFormRepository
  initialName?: string
  onCancel: () => void
  onPendingChange?: (isPending: boolean) => void
  onSuccess: (label: InlineLabelFormLabel) => void
  variant?: InlineLabelFormVariant
}

function getCreateLabelErrorMessage(error: unknown) {
  if (error instanceof TypeError || error instanceof SyntaxError) return LABELS.labelCreateError
  return error instanceof Error && error.message.trim() ? error.message : LABELS.labelCreateError
}

export function InlineLabelForm({
  repository,
  initialName = '',
  onCancel,
  onPendingChange,
  onSuccess,
  variant = InlineLabelFormVariant.Default,
}: InlineLabelFormProps) {
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState('')
  const [color, setColor] = useState(randomHexColor)
  const [colorInputValue, setColorInputValue] = useState(color)
  const [nameValidationError, setNameValidationError] = useState<string | null>(null)
  const [colorValidationError, setColorValidationError] = useState<string | null>(null)
  const [hasValidatedName, setHasValidatedName] = useState(false)
  const [hasValidatedColor, setHasValidatedColor] = useState(false)
  const [submissionError, setSubmissionError] = useState('')
  const nameInputRef = useRef<HTMLInputElement>(null)
  const colorInputRef = useRef<HTMLInputElement>(null)
  const errorBannerRef = useRef<HTMLDivElement>(null)
  const {mutateAsync: createLabel, isPending} = useCreateLabelMutation()

  useEffect(() => {
    nameInputRef.current?.focus()
  }, [])

  const handleColorChange = (newColor: string) => {
    setColorInputValue(newColor)
    if (hasValidatedColor) {
      setColorValidationError(validateLabelColor(newColor))
    }
    if (isValidColor(newColor)) {
      setColor(normalizeHexColor(newColor))
    }
  }

  const handleNameChange = (newName: string) => {
    setName(newName)
    if (hasValidatedName) {
      setNameValidationError(validateLabelForm(newName, colorInputValue).nameError)
    }
  }

  const handleSubmit = async () => {
    if (isPending) return

    setNameValidationError(null)
    setColorValidationError(null)
    setSubmissionError('')

    const {nameError, colorError} = validateLabelForm(name, colorInputValue)
    setHasValidatedName(true)
    if (nameError) {
      setNameValidationError(nameError)
      nameInputRef.current?.focus()
      return
    }

    setHasValidatedColor(true)
    if (colorError) {
      setColorValidationError(colorError)
      colorInputRef.current?.focus()
      return
    }

    let label: InlineLabelFormLabel
    onPendingChange?.(true)
    try {
      const createdLabel = await createLabel({
        repositoryId: repository.id,
        name: name.trim(),
        description: description.trim(),
        color: color.replace('#', ''),
      })

      if (!createdLabel) throw new Error(LABELS.labelCreateError)
      label = createdLabel
    } catch (error) {
      setSubmissionError(getCreateLabelErrorMessage(error))
      requestAnimationFrame(() => errorBannerRef.current?.focus())
      return
    } finally {
      onPendingChange?.(false)
    }

    onSuccess(label)
  }

  return (
    <form
      className={clsx(styles.dialogFormBody)}
      data-variant={variant}
      noValidate
      onSubmit={event => {
        event.preventDefault()
        void handleSubmit()
      }}
    >
      {submissionError ? (
        <Banner
          ref={errorBannerRef}
          title="Error"
          description={<AriaAlert>{submissionError}</AriaAlert>}
          variant="critical"
          role="alert"
          tabIndex={-1}
        />
      ) : null}
      <div className={styles.dialogFormPreviewContainer}>
        <LabelPreview name={name || LABELS.labelPreview} color={color.replace('#', '')} />
      </div>
      <FormControl className={styles.dialogFormInput} id="inline-label-form-name">
        <FormControl.Label requiredText=" (required)">{LABELS.name}</FormControl.Label>
        <TextInput
          ref={nameInputRef}
          value={name}
          onChange={event => handleNameChange(event.target.value)}
          className={styles.dialogFormInput}
          placeholder={LABELS.labelNamePlaceholder}
          readOnly={isPending}
          aria-disabled={isPending}
          aria-required="true"
        />
        <FormControl.Caption>{LABELS.nameCharacterLimit}</FormControl.Caption>
        {nameValidationError ? (
          <FormControl.Validation variant="error">{nameValidationError}</FormControl.Validation>
        ) : null}
      </FormControl>
      <FormControl className={styles.dialogFormInput} id="inline-label-form-description">
        <FormControl.Label>{LABELS.description}</FormControl.Label>
        <Textarea
          value={description}
          onChange={event => setDescription(event.target.value)}
          rows={2}
          className={styles.dialogFormInput}
          placeholder={LABELS.labelDescriptionPlaceholder}
          readOnly={isPending}
          aria-disabled={isPending}
        />
      </FormControl>
      <div className={styles.dialogFormInput}>
        <LabelColorPicker
          color={color}
          onChangeCallback={handleColorChange}
          ref={colorInputRef}
          disabled={isPending}
          required
          validationError={colorValidationError}
          fullWidth={variant === InlineLabelFormVariant.Picker}
        />
      </div>
      <div className={styles.dialogFormButtonGroup}>
        <Button type="button" onClick={isPending ? undefined : onCancel} aria-disabled={isPending}>
          {LABELS.cancelButtonText}
        </Button>
        <Button type="submit" variant="primary" loading={isPending}>
          {LABELS.createButtonText}
        </Button>
      </div>
    </form>
  )
}

InlineLabelForm.displayName = 'InlineLabelForm'
